import { AxiosResponse } from "axios";
import { normalize, schema } from 'normalizr';
import { createAsyncThunk } from "@reduxjs/toolkit";
import communityApi from '../../api/communityApi';
import { CategoryInterface, initialInterface } from "../../store/community/categoriesSlice";
import communityUrls from "../../configs/community/communityUrls";

const PREFIX = 'community';

const processStrategy = (value: any) => {
  return {
    ...value,
    thumbnail_url: communityUrls.remoteUrl(value.thumbnail_url)
  };
}

const categoriesEntity = new schema.Entity(
  'categories',
  {},
  {
    processStrategy
  }
);

const categoryEntity = new schema.Entity(
  'category',
  {},
  {
    processStrategy
  }
);

const normalizeInstance = (response: AxiosResponse) => {
  const normalized = normalize<
    any,
    {
      category: CategoryInterface
    }
  >(response.data.category, categoryEntity);

  return normalized.entities;
}

const normalizeCollection = (response: AxiosResponse, silentId?: number) => {
  const normalized = normalize<
    any,
    {
      categories: { [key: string]: CategoryInterface };
    }
  >(response.data.categories, [categoriesEntity]);

  return {
    categories: normalized.entities?.categories || {},
    ids: normalized.result,
    count: response.data.meta?.total_count,
    silentId: silentId
  };
}

export const fetchCategories = createAsyncThunk(
  `${PREFIX}_categories/fetchCategories`,
  async ({ params, silentId }: { params: object, silentId?: number }) => {
    const response = await communityApi.get(`/onesky/categories`, { params })

    return normalizeCollection(response, silentId);
  }
);

export const fetchCategory = createAsyncThunk(
  `${PREFIX}_categories/fetchCategory`,
  async (id: number) => {
    const response = await communityApi.get(`/onesky/categories/${id}`)

    return normalizeInstance(response);
  }
);

export const createCategory = createAsyncThunk(
  `${PREFIX}_categories/createCategory`,
  async (data: object, { rejectWithValue }) => {
    try {
      const response = await communityApi.post(`/onesky/categories`, data)

      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const updateCategory = createAsyncThunk(
  `${PREFIX}_categories/updateCategory`,
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await communityApi.patch(`/onesky/categories/${id}`, data)

      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const destroyCategory = createAsyncThunk(
  `${PREFIX}_categories/destroyCategory`,
  async (id: number) => {
    const response = await communityApi.delete(`/onesky/categories/${id}`)

    return response.data;
  }
);

export const reorderCategories = createAsyncThunk(
  `${PREFIX}_categories/reorderCategories`,
  async ({ ids }: { ids: any }, { rejectWithValue }) => {
    try {
      await communityApi.patch('/onesky/categories/reorder', { ids })

      return { ids };
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);
